import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { supabase } from '@/integrations/supabase/client';

const supabaseAny = supabase as any;

interface QueueItem {
  id?: number;
  entity: string;
  idempotencyKey: string;
  payloadJson: any;
  files?: { name: string; blob: Blob; path: string }[];
  deviceSubmittedAt: string;
  retryCount: number;
  lastError?: string;
  createdAt: string;
  userId: string;
}

interface OfflineDB extends DBSchema {
  queue: {
    key: number;
    value: QueueItem;
    indexes: {
      'by-entity': string;
      'by-user': string;
      'by-created': string;
    };
  };
  blobs: {
    key: string;
    value: {
      key: string;
      blob: Blob;
      name: string;
      queueId: number;
      path?: string;
      bucket?: string;
      kind?: 'signature' | 'pdf';
      questionKey?: string;
    };
  };
}

let db: IDBPDatabase<OfflineDB> | null = null;
// Mutex flag: prevents concurrent processQueue() calls from double-sending items
let isProcessing = false;

/** @internal – exposed only for test cleanup */
export function _resetProcessingFlag() {
  isProcessing = false;
}

function isStorageAlreadyExistsError(error: any): boolean {
  const message = String(error?.message || '').toLowerCase();
  const code = String(error?.code || '').toLowerCase();
  const statusCode = Number(error?.statusCode ?? error?.status ?? 0);

  return (
    statusCode === 409 ||
    code === '409' ||
    message.includes('already exists') ||
    message.includes('resource already exists') ||
    message.includes('the resource already exists')
  );
}

export async function initOfflineDB() {
  if (db) return db;

  db = await openDB<OfflineDB>('offline-queue-db', 1, {
    upgrade(db) {
      const queueStore = db.createObjectStore('queue', {
        keyPath: 'id',
        autoIncrement: true,
      });
      queueStore.createIndex('by-entity', 'entity');
      queueStore.createIndex('by-user', 'userId');
      queueStore.createIndex('by-created', 'createdAt');

      db.createObjectStore('blobs', { keyPath: 'key' });
    },
  });

  return db;
}

export async function queueOfflineWrite(params: {
  entity: string;
  payload: any;
  files?: File[];
  userId: string;
}) {
  const db = await initOfflineDB();
  const { entity, payload, files, userId } = params;

  const idempotencyKey = generateIdempotencyKey(entity, userId);
  const deviceSubmittedAt = new Date().toISOString();

  const queueId = await db.add('queue', {
    entity,
    idempotencyKey,
    payloadJson: payload,
    deviceSubmittedAt,
    retryCount: 0,
    createdAt: new Date().toISOString(),
    userId,
  });

  if (files && files.length > 0) {
    for (const file of files) {
      const blobKey = `${idempotencyKey}_${file.name}`;
      await db.put('blobs', {
        key: blobKey,
        blob: file,
        name: file.name,
        queueId,
      });
    }
  }

  console.log(`[OfflineQueue] Queued ${entity} with key ${idempotencyKey}`);

  if (navigator.onLine) {
    processQueue();
  }

  return idempotencyKey;
}

export async function processQueue() {
  // Guard against concurrent invocations (e.g. rapid online events or overlapping timers)
  if (isProcessing) {
    console.log('[OfflineQueue] Already processing, skipping concurrent invocation');
    return;
  }
  isProcessing = true;

  try {
    const db = await initOfflineDB();
    const items = await db.getAllFromIndex('queue', 'by-created');

    console.log(`[OfflineQueue] Processing ${items.length} queued items`);

    for (const item of items) {
      try {
        await syncQueueItem(item);
        await db.delete('queue', item.id!);

        // Delete only blobs that belong to this item
        const allBlobs = await db.getAll('blobs');
        for (const blob of allBlobs) {
          if (blob.queueId === item.id) {
            await db.delete('blobs', blob.key);
          }
        }
      } catch (error: any) {
        const nextRetryCount = item.retryCount + 1;
        console.error(`[OfflineQueue] Sync failed (attempt ${nextRetryCount}) for ${item.entity}:`, error);

        if (nextRetryCount >= 5) {
          // Move to audit log as a failed entry rather than silently dropping
          console.error(`[OfflineQueue] Max retries exceeded for ${item.idempotencyKey} — moving to failed audit`);
          await supabase.from('offline_audit').insert({
            user_id: item.userId,
            entity: item.entity,
            idempotency_key: item.idempotencyKey,
            payload_json: item.payloadJson,
            attempts: nextRetryCount,
            last_status: 'failed',
            last_error: error?.message ?? String(error),
            synced_at: new Date().toISOString(),
          }).then(({ error: auditError }) => {
            if (auditError) console.error('[OfflineQueue] Failed to write failed-audit entry:', auditError);
          });
          await db.delete('queue', item.id!);
        } else {
          await db.put('queue', {
            ...item,
            retryCount: nextRetryCount,
            lastError: error.message,
          });
        }
      }
    }
  } finally {
    isProcessing = false;
  }
}

async function syncQueueItem(item: QueueItem) {
  const db = await initOfflineDB();

  const uploadedByKind: Record<string, string> = {};
  const legacyUploads: string[] = [];
  const allBlobs = await db.getAll('blobs');
  const itemBlobs = allBlobs.filter((b) => b.queueId === item.id);

  for (const blobEntry of itemBlobs) {
    const isSignature = item.entity === 'signatures';
    const bucket = blobEntry.bucket || (isSignature ? 'signatures' : 'offline-sync');
    const uploadPath =
      blobEntry.path ||
      (isSignature
        ? `${item.userId}/${Date.now()}_${blobEntry.name}`
        : `${item.entity}/${item.userId}/${Date.now()}_${blobEntry.name}`);

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(uploadPath, blobEntry.blob, {
        cacheControl: '3600',
        upsert: false,
      });

    let resolvedPath = data?.path ?? uploadPath;
    if (error) {
      if (!isStorageAlreadyExistsError(error)) throw error;
      resolvedPath = uploadPath;
      console.info('[OfflineQueue]', {
        event: 'storage_upload_already_exists_treated_as_success',
        entity: item.entity,
        bucket,
        path: uploadPath,
      });
    }

    if (blobEntry.kind) {
      uploadedByKind[blobEntry.kind] = resolvedPath;
    } else {
      legacyUploads.push(resolvedPath);
    }
  }

  const payload = {
    ...item.payloadJson,
    device_submitted_at: item.deviceSubmittedAt,
  };

  if (legacyUploads.length > 0) {
    if (item.entity === 'evidence') {
      payload.file_url = legacyUploads[0];
    } else if (item.entity === 'reimbursements') {
      payload.receipt_url = legacyUploads[0];
    }
  }

  switch (item.entity) {
    case 'clock_events':
      await syncClockEvent(payload);
      break;
    case 'shift_assignments':
      await syncShiftAssignment(payload);
      break;
    case 'campaign_assignments':
      await syncCampaignAssignment(payload);
      break;
    case 'evidence':
      await syncEvidence(payload);
      break;
    case 'report_responses':
      await syncReportResponse(payload);
      break;
    case 'consumer_sessions':
      await syncConsumerSession(payload);
      break;
    case 'scratch_consume_token':
      await syncScratchConsumeToken(payload);
      break;
    case 'scratch_confirm_redemption':
      await syncScratchConfirmRedemption(payload);
      break;
    case 'inventory_checkouts':
      await syncInventoryCheckout(payload);
      break;
    case 'reimbursements':
      await syncReimbursement(payload);
      break;
    case 'signatures':
      await syncSignature(payload, uploadedByKind, legacyUploads);
      break;
    default:
      throw new Error(`Unknown entity: ${item.entity}`);
  }

  await supabase.from('offline_audit').insert({
    user_id: item.userId,
    entity: item.entity,
    idempotency_key: item.idempotencyKey,
    payload_json: payload,
    attempts: item.retryCount + 1,
    last_status: 'success',
    synced_at: new Date().toISOString(),
  });

  console.log(`[OfflineQueue] Successfully synced ${item.entity} (${item.idempotencyKey})`);
}

async function syncClockEvent(payload: any) {
  // Whitelist columns to prevent arbitrary column injection from tampered IndexedDB
  const {
    ba_id, campaign_id, store_id, type, occurred_at,
    lat, lng, distance_m, device_submitted_at, assignment_id, shift_id, checkin_session_id,
  } = payload;
  const { error } = await supabaseAny.from('clock_events').insert({
    ba_id, campaign_id, store_id, type, occurred_at,
    lat, lng, distance_m, device_submitted_at, assignment_id, shift_id, checkin_session_id,
  });
  if (error) throw error;
}

async function syncShiftAssignment(payload: any) {
  const { id, status, responded_at, device_submitted_at } = payload;
  const { error } = await supabase
    .from('shift_assignments')
    .update({ status, responded_at, device_submitted_at })
    .eq('id', id);
  if (error) throw error;
}

async function syncCampaignAssignment(payload: any) {
  const { id, status, accepted_at, device_submitted_at } = payload;
  const { error } = await supabase
    .from('campaign_assignments')
    .update({ status, accepted_at, device_submitted_at })
    .eq('id', id);
  if (error) throw error;
}

async function syncEvidence(payload: any) {
  const {
    ba_id,
    campaign_id,
    store_id,
    shift_id,
    assignment_id,
    checkin_session_id,
    promoter_user_id,
    device_id,
    file_url,
    notes,
    kind,
    evidence_type,
    captured_at,
    session_id,
    device_submitted_at,
  } = payload;
  const { error } = await supabaseAny.from('evidence').insert({
    ba_id,
    campaign_id,
    store_id,
    shift_id,
    assignment_id,
    checkin_session_id,
    promoter_user_id,
    device_id,
    file_url,
    notes,
    kind,
    evidence_type,
    captured_at,
    session_id,
    device_submitted_at,
  });
  if (error) throw error;
}

async function syncReportResponse(payload: any) {
  const {
    id,
    campaign_id,
    org_id,
    report_sheet_id,
    report_sheet_version_id,
    campaign_report_sheet_id,
    ba_id,
    store_id,
    shift_id,
    assignment_id,
    promoter_user_id,
    checkin_session_id,
    data_json,
    visible_question_keys,
    render_snapshot_json,
    status,
    submitted_at,
    client_started_at,
    client_submitted_at,
    session_id,
    survey_stage,
    device_submitted_at,
  } = payload;
  const { error } = await supabaseAny.from('report_responses').upsert(
    {
      id,
      campaign_id,
      org_id,
      report_sheet_id,
      report_sheet_version_id,
      campaign_report_sheet_id,
      ba_id,
      store_id,
      shift_id,
      assignment_id,
      promoter_user_id,
      checkin_session_id,
      data_json,
      visible_question_keys,
      render_snapshot_json,
      status,
      submitted_at,
      client_started_at,
      client_submitted_at,
      session_id,
      survey_stage,
      device_submitted_at,
    },
    { onConflict: 'id' },
  );
  if (error) throw error;
}

async function syncConsumerSession(payload: any) {
  const { error } = await supabaseAny.from('consumer_sessions').insert(payload);
  if (error) throw error;
}

async function syncScratchConsumeToken(payload: any) {
  const { error } = await supabaseAny.rpc('consume_token_and_reserve_award', payload);
  if (error) throw error;
}

async function syncScratchConfirmRedemption(payload: any) {
  const { error } = await supabaseAny.rpc('confirm_redemption_for_session', payload);
  if (error) throw error;
}

async function syncInventoryCheckout(payload: any) {
  const { error } = await supabaseAny.from('inventory_checkouts').insert(payload);
  if (error) throw error;
}

async function syncReimbursement(payload: any) {
  const { error } = await supabase.from('reimbursements').insert(payload);
  if (error) throw error;
}

async function syncSignature(
  payload: any,
  uploadedByKind: Record<string, string>,
  legacyUploads: string[]
) {
  const { acceptance, signed_acceptance, ...signaturePayload } = payload;
  const signatureUpload = uploadedByKind.signature || legacyUploads[0];
  const pdfUpload = uploadedByKind.pdf || legacyUploads[1];

  if (signatureUpload) {
    signaturePayload.signature_image_url = signatureUpload;
  }
  if (pdfUpload) {
    signaturePayload.file_url = pdfUpload;
  }

  const { data: existing } = await (supabase as any)
    .from('signatures')
    .select('id, device_submitted_at, signature_image_url')
    .eq('user_id', signaturePayload.user_id)
    .eq('contract_id', signaturePayload.contract_id)
    .eq('is_valid', true)
    .maybeSingle();

  let signatureId: string | null = existing?.id ?? null;
  let signatureImageUrl = signaturePayload.signature_image_url;

  if (existing) {
    const existingTime = new Date(existing.device_submitted_at);
    const newTime = new Date(signaturePayload.device_submitted_at);

    if (newTime < existingTime) {
      await (supabase as any)
        .from('signatures')
        .update({
          is_valid: false,
          invalidated_at: new Date().toISOString(),
          invalidated_reason: 'Replaced by earlier offline signature',
        })
        .eq('user_id', signaturePayload.user_id)
        .eq('contract_id', signaturePayload.contract_id)
        .eq('is_valid', true);

      const { data: inserted, error } = await (supabase as any)
        .from('signatures')
        .insert(signaturePayload)
        .select('id, signature_image_url')
        .single();

      if (error) throw error;

      signatureId = inserted?.id ?? null;
      signatureImageUrl = inserted?.signature_image_url ?? signatureImageUrl;
      console.log('[OfflineQueue] Replaced existing signature with earlier one');
    } else {
      signatureImageUrl = existing.signature_image_url ?? signatureImageUrl;
      console.log('[OfflineQueue] Signature already exists with earlier timestamp, discarding');
    }
  } else {
    const { data: inserted, error } = await (supabase as any)
      .from('signatures')
      .insert(signaturePayload)
      .select('id, signature_image_url')
      .single();

    if (error) throw error;
    signatureId = inserted?.id ?? null;
    signatureImageUrl = inserted?.signature_image_url ?? signatureImageUrl;
  }

  if (acceptance) {
    const { contract_version: _legacyContractVersion, ...acceptanceBase } = acceptance;
    const acceptancePayload = {
      ...acceptanceBase,
      signature_id: signatureId ?? acceptanceBase.signature_id ?? null,
      signature_url: acceptanceBase.signature_url || signatureImageUrl || null,
    };

    if (signatureUpload) {
      acceptancePayload.signature_storage_path = signatureUpload;
    }
    if (pdfUpload) {
      acceptancePayload.signed_pdf_storage_path = pdfUpload;
    }

    const { error } = await (supabase as any)
      .from('ba_contract_acceptances')
      .upsert(acceptancePayload, {
        onConflict: 'ba_id,campaign_id,contract_template_id',
      });

    if (error) throw error;
  }

  if (signed_acceptance) {
    const signedAcceptancePayload = {
      ...signed_acceptance,
      signature_url: signed_acceptance.signature_url || signatureImageUrl || null,
    };

    const { error } = await (supabase as any)
      .from('signed_contract_acceptances')
      .upsert(signedAcceptancePayload, {
        onConflict: 'ba_id,contract_version_id',
      });

    if (error) throw error;
  }
}

function generateIdempotencyKey(entity: string, userId: string): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `${entity}_${userId}_${timestamp}_${random}`;
}

window.addEventListener('online', () => {
  console.log('[OfflineQueue] Network restored, processing queue...');
  processQueue();
});

window.addEventListener('offline', () => {
  console.log('[OfflineQueue] Network lost, queueing enabled');
});

export async function queueSignature(params: {
  payload: any;
  acceptancePayload?: any;
  signedAcceptancePayload?: any;
  signatureImageBlob: Blob | null;
  pdfBlob: Blob;
  userId: string;
  contractTemplateId: string;
  signaturePath?: string | null;
  pdfPath?: string;
}) {
  const db = await initOfflineDB();
  const {
    payload,
    acceptancePayload,
    signedAcceptancePayload,
    signatureImageBlob,
    pdfBlob,
    userId,
    contractTemplateId,
    signaturePath: providedSignaturePath,
    pdfPath: providedPdfPath,
  } = params;

  let idempotencyKey = generateIdempotencyKey('signatures', userId);
  const deviceSubmittedAt = new Date().toISOString();

  const fullPayload = {
    ...payload,
    acceptance: acceptancePayload,
    signed_acceptance: signedAcceptancePayload,
    device_submitted_at: deviceSubmittedAt,
  };

  const existingItems = await db.getAllFromIndex('queue', 'by-user', userId);
  const existingSignatureItem = existingItems
    .filter((item) => item.entity === 'signatures')
    .filter((item) => {
      const existingPayload = item.payloadJson || {};
      const existingAcceptance = existingPayload.acceptance || {};
      return (
        existingPayload.contract_id === payload.contract_id &&
        existingPayload.user_id === payload.user_id &&
        existingAcceptance.contract_template_id === acceptancePayload?.contract_template_id
      );
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

  let queueId: number;
  if (existingSignatureItem?.id) {
    idempotencyKey = existingSignatureItem.idempotencyKey;
    queueId = existingSignatureItem.id;
    await db.put('queue', {
      ...existingSignatureItem,
      payloadJson: fullPayload,
      deviceSubmittedAt,
      retryCount: 0,
      lastError: undefined,
      createdAt: new Date().toISOString(),
    });

    const existingBlobs = await db.getAll('blobs');
    for (const blob of existingBlobs) {
      if (blob.queueId === queueId) {
        await db.delete('blobs', blob.key);
      }
    }

    console.info('[OfflineQueue]', {
      event: 'reused_pending_signature_queue_item',
      queue_id: queueId,
      idempotency_key: idempotencyKey,
      user_id: userId,
      contract_id: payload.contract_id,
      contract_template_id: acceptancePayload?.contract_template_id ?? null,
    });
  } else {
    queueId = await db.add('queue', {
      entity: 'signatures',
      idempotencyKey,
      payloadJson: fullPayload,
      deviceSubmittedAt,
      retryCount: 0,
      createdAt: new Date().toISOString(),
      userId,
    });
  }

  const signaturePath =
    providedSignaturePath ||
    (signatureImageBlob ? `${userId}/${contractTemplateId}_${Date.now()}.png` : null);

  if (signatureImageBlob && signaturePath) {
    await db.put('blobs', {
      key: `${idempotencyKey}_signature`,
      blob: signatureImageBlob,
      name: 'signature.png',
      queueId,
      path: signaturePath,
      bucket: 'signatures',
      kind: 'signature',
    });
  }

  const pdfPath = providedPdfPath || `${userId}/${contractTemplateId}_${Date.now()}.pdf`;
  await db.put('blobs', {
    key: `${idempotencyKey}_pdf`,
    blob: pdfBlob,
    name: 'signed_contract.pdf',
    queueId,
    path: pdfPath,
    bucket: 'signed-contracts',
    kind: 'pdf',
  });

  console.log(`[OfflineQueue] Queued signature ${idempotencyKey}`);

  if (navigator.onLine) {
    processQueue();
  }

  return idempotencyKey;
}

export async function queueSurveyResponse(params: {
  payload: any;
  uploads: Array<{ file: File; path: string }>;
  userId: string;
}) {
  const db = await initOfflineDB();
  const { payload, uploads, userId } = params;

  const idempotencyKey = generateIdempotencyKey('report_responses', userId);
  const deviceSubmittedAt = new Date().toISOString();

  const queueId = await db.add('queue', {
    entity: 'report_responses',
    idempotencyKey,
    payloadJson: payload,
    deviceSubmittedAt,
    retryCount: 0,
    createdAt: new Date().toISOString(),
    userId,
  });

  for (const upload of uploads) {
    await db.put('blobs', {
      key: `${idempotencyKey}_${upload.path}`,
      blob: upload.file,
      name: upload.file.name,
      queueId,
      path: upload.path,
      bucket: 'offline-sync',
    });
  }

  console.log(`[OfflineQueue] Queued report_responses ${idempotencyKey}`);

  if (navigator.onLine) {
    processQueue();
  }

  return idempotencyKey;
}

export async function getPendingSignatures(userId: string) {
  const db = await initOfflineDB();
  const allItems = await db.getAllFromIndex('queue', 'by-user', userId);
  return allItems.filter(item => item.entity === 'signatures');
}

export async function cancelQueuedSignature(queueId: number) {
  const db = await initOfflineDB();
  
  await db.delete('queue', queueId);
  
  const allBlobs = await db.getAll('blobs');
  for (const blob of allBlobs) {
    if (blob.queueId === queueId) {
      await db.delete('blobs', blob.key);
    }
  }
  
  console.log(`[OfflineQueue] Cancelled signature queue item ${queueId}`);
}

export async function retryQueuedSignature(queueId: number) {
  const db = await initOfflineDB();
  const item = await db.get('queue', queueId);
  
  if (!item) return;
  
  await db.put('queue', {
    ...item,
    retryCount: 0,
    lastError: undefined,
  });
  
  console.log(`[OfflineQueue] Retrying signature ${item.idempotencyKey}`);
  
  if (navigator.onLine) {
    processQueue();
  }
}
